/* eslint-disable react/no-array-index-key */
import { Fragment, type ReactNode } from 'react';
import { Group } from '@mantine/core';

import Title from '@/components/content/title/Title';
import DrawerLink from '../../drawerlink/DrawerLink';
import IssueIDLinkv2 from '../../issueIdLink/IssueIdLink';
import Markdown from '@/components/content/markdown/Markdown';
import { POSTHOG_EVENTS } from '@/lib/posthog/events';
import { trackEvent } from '@/lib/posthog/helpers';

import type { Tracker } from '@/constants/mercuryPages';

import classes from './Results.module.css';

interface BaseResultProps {
  title: { id: string | number; name: string };
  tracker?: Tracker;
  content: string | null;
  drawerContent?: ReactNode;
  customLink?: ReactNode;
  footer: Array<ReactNode>;
}

const BaseResult = ({
  title: { id, name },
  content,
  tracker,
  drawerContent,
  customLink,
  footer,
}: BaseResultProps) => {
  const entityType = tracker?.tracker_name ?? null;

  const handleResultClick = () => {
    trackEvent(POSTHOG_EVENTS.SEARCH_GLOBAL_RESULT_CLICK, {
      entityId: String(id),
      entityType,
    });

    if (drawerContent) {
      trackEvent(POSTHOG_EVENTS.SEARCH_GLOBAL_SIDEPANEL_OPEN, {
        entityId: String(id),
        entityType,
      });
    }
  };

  return (
    <div className={classes.resultWrapper} data-testid="search-result">
      <Title order={3} size="h4">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <span onClickCapture={handleResultClick}>
          {customLink && customLink}

          {drawerContent && (
            <DrawerLink
              id={id}
              value={`${id} | ${name}`}
              content={drawerContent}
              link={
                tracker && typeof id === 'number' ? (
                  <IssueIDLinkv2 value={id} name="See More" tracker={tracker} useParent />
                ) : null
              }
            />
          )}

          {!drawerContent && !customLink && typeof id === 'number' && (
            <IssueIDLinkv2 value={id} name={`${id} | ${name}`} tracker={tracker} useParent />
          )}
        </span>
      </Title>

      {content && (
        <Markdown
          text={content}
          disallowedElements={['table', 'img', 'section']}
          textProps={{
            lineClamp: 2,
            mt: 15,
            classNames: { root: classes.descriptionText },
          }}
        />
      )}

      {footer.length > 0 && (
        <Group mt={25} align="center">
          {footer.map((item, index) => (
            <Fragment key={`result-footer-item-id-${index}`}>{item}</Fragment>
          ))}
        </Group>
      )}
    </div>
  );
};

export default BaseResult;
